import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest } from '@data/controller/NetworkController';
import { ChurchAssetUploadTarget } from '@interfaces/church.interfaces';
import { blurHashForThumbnail } from '@utility/blurHash';

// What the image is for; Sanctuary checks the uploader's role against it.
// Both need a Service Manager.
export type LibraryThumbnailAssetType = 'service' | 'series';

export type UploadedThumbnail = {
  url: string;
  // Null when one couldn't be worked out; the upload still counts.
  blurHash: string | null;
};

const UPLOAD_FAILED = "The thumbnail couldn't be uploaded. Please try again.";

// Uploads a service or series thumbnail through Sanctuary's upload target, and
// works out its BlurHash from the file at the same time, so it adds no wait.
// The URL it returns is the only kind Sanctuary accepts as a thumbnail.
export async function uploadLibraryThumbnail(
  churchID: string,
  file: File,
  assetType: LibraryThumbnailAssetType,
  auth: AuthContextType
): Promise<{ thumbnail?: UploadedThumbnail; error?: string }> {
  const blurHash = blurHashForThumbnail(file);
  const contentType = file.type || 'application/octet-stream';

  const target = await NetworkController.performRequestAt<ChurchAssetUploadTarget>(
    process.env.REACT_APP_SANCTUARY_API_URL || '',
    new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/assets/upload-target`, {
      assetType,
      contentType,
      fileSizeBytes: file.size
    }),
    auth.consumeRefreshToken
  );

  if (!target.body) {
    return { error: target.error || UPLOAD_FAILED };
  }

  const uploaded = await NetworkController.performSignedPost(
    { url: target.body.url, fields: target.body.fields },
    file,
    contentType
  );

  if (!uploaded) {
    return { error: UPLOAD_FAILED };
  }

  return { thumbnail: { url: target.body.assetURL, blurHash: await blurHash } };
}
